import React, { useState, useMemo } from 'react';
import { Table, Button, Space, Tag, Tooltip, Input, Card } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, EyeOutlined } from '@ant-design/icons';
import ExportExcelButton from './ExportExcelButton';
import debounce from 'lodash/debounce';

const { Search } = Input;

const TabelaDados = ({
                         data,
                         extraColumns,
                         onModalOpen,
                         link_visualizar = null,
                         editarvisualizar,
                         excluirvisualizar,
                         adicionarvisualizar
                     }) => {
    const [searchText, setSearchText] = useState('');
    const [sortedInfo, setSortedInfo] = useState({});
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const handleAdd = () => onModalOpen('add', null);
    const handleEdit = (record) => onModalOpen('edit', record.id);
    const handleDelete = (record) => onModalOpen('delete', record.id);

    const handleSearch = debounce((value) => {
        setSearchText(value);
        setCurrentPage(1);
    }, 300);

    const getStatusTagClasses = (status) => {
        const lowerStatus = status?.toLowerCase() || '';
        if (lowerStatus === 'ativo' || lowerStatus === 'sucesso') {
            return 'bg-green-100 text-green-700 border-green-200';
        }
        if (lowerStatus === 'inativo' || lowerStatus === 'bloqueado' || lowerStatus === 'falha') {
            return 'bg-red-100 text-red-700 border-red-200';
        }
        return 'bg-gray-100 text-gray-700 border-gray-200';
    };

    const filteredData = useMemo(() => {
        if (!searchText) return data;
        const lowerSearchText = searchText.toLowerCase();
        return data.filter(item =>
            Object.values(item).some(value =>
                value && value.toString().toLowerCase().includes(lowerSearchText)
            )
        );
    }, [data, searchText]);

    const compareValues = (a, b) => {
        if (a == null) return -1;
        if (b == null) return 1;
        return String(a).localeCompare(String(b), undefined, { numeric: true });
    };

    const columns = useMemo(() => {
        const baseColumns = [
            { title: 'ID', dataIndex: 'id', key: 'id', width: 80, align: 'center', sorter: (a, b) => compareValues(a.id, b.id) },
            ...extraColumns.map(col => ({
                title: col.name,
                dataIndex: col.id,
                key: col.id,
                width: col.width || 150,
                sorter: col.sort ? (a, b) => compareValues(a[col.id], b[col.id]) : undefined,
                render: (text) => (
                    <Tooltip title={text} placement="topLeft">
                        <div className="truncate">{text}</div>
                    </Tooltip>
                ),
            })),
            {
                title: 'Situação',
                dataIndex: 'status',
                key: 'status',
                align: 'center',
                width: 120,
                sorter: (a, b) => compareValues(a.status, b.status),
                render: (status) => (
                    <Tag className={`min-w-[85px] text-center uppercase text-xs font-semibold px-2 py-1 rounded-md border ${getStatusTagClasses(status)}`}>
                        {status}
                    </Tag>
                ),
            },
        ];

        const hasAnyAction = link_visualizar || editarvisualizar !== false || excluirvisualizar !== false;
        if (hasAnyAction) {
            baseColumns.push({
                title: 'Ações',
                key: 'actions',
                width: 120,
                align: 'center',
                render: (_, record) => (
                    <Space size="middle">
                        {link_visualizar && (
                            <Tooltip title="Visualizar"><Button type="link" icon={<EyeOutlined />} onClick={() => window.open(`${link_visualizar}/${record.id}`, '_blank')} /></Tooltip>
                        )}
                        {editarvisualizar !== false && (
                            <Tooltip title="Editar"><Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} /></Tooltip>
                        )}
                        {excluirvisualizar !== false && (
                            <Tooltip title="Excluir"><Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} /></Tooltip>
                        )}
                    </Space>
                ),
            });
        }

        return baseColumns;

    }, [sortedInfo, extraColumns, link_visualizar, editarvisualizar, excluirvisualizar]);

    return (
        <Card className="shadow-none">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4">
                <Search
                    placeholder="Buscar..."
                    allowClear
                    onChange={e => handleSearch(e.target.value)}
                    className="w-full sm:w-auto sm:max-w-xs"
                />
                <Space>
                    <ExportExcelButton data={filteredData} columns={columns} />
                    {adicionarvisualizar !== false && (
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>Adicionar</Button>
                    )}
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                pagination={{ current: currentPage, pageSize: pageSize, onChange: (page, size) => { setCurrentPage(page); setPageSize(size); }, showSizeChanger: true, pageSizeOptions: ['10', '25', '50'] }}
                scroll={{ x: 'max-content' }}
                size="middle"
                className="ant-table-wrapper"
            />
        </Card>
    );
};

export default TabelaDados;